import { hydrateScheduleCard, normalizeExecutionSummary, normalizeWorkflowDefinition, } from './workflowApiAdapters';
import { buildWorkflowTenantHeaders } from './workflowTenantHeaders';
// APIクライアント - バックエンドとの通信を管理
class ApiClient {
    constructor(baseUrl = window.location.origin) {
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "token", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "tenantId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "refreshAuthHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "workflowViewOverrides", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        // 認証API
        Object.defineProperty(this, "auth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                login: (username, password) => this.request('/auth/login', {
                    method: 'POST',
                    body: JSON.stringify({ username, password }),
                }),
                register: (username, email, password) => this.request('/auth/register', {
                    method: 'POST',
                    body: JSON.stringify({ username, email, password }),
                }),
                logout: (refreshToken) => this.request('/auth/logout', {
                    method: 'POST',
                    body: JSON.stringify({ refreshToken }),
                }),
                // テスト用エンドポイント（ルーティング確認用）
                test: () => {
                    return this.request('/auth/test');
                },
                // 詳細ユーザー情報取得（detail=fullパラメーター使用）
                getDetail: () => {
                    return this.request('/auth/user/detail');
                },
            }
        });
        // Hishaku設定管理API
        Object.defineProperty(this, "hishaku", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // 基本CRUD
                getConfigs: () => this.request('/api/hishaku/configs'),
                getConfig: (name) => this.request(`/api/hishaku/configs/${name}`),
                createConfig: (config) => this.request('/api/hishaku/configs', {
                    method: 'POST',
                    body: JSON.stringify(config),
                }),
                updateConfig: (name, config) => this.request(`/api/hishaku/configs/${name}`, {
                    method: 'PUT',
                    body: JSON.stringify(config),
                }),
                deleteConfig: (name) => this.request(`/api/hishaku/configs/${name}`, {
                    method: 'DELETE',
                }),
                // バージョン管理
                getVersions: (name, params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/hishaku/configs/${name}/versions${query}`);
                },
                // バックアップ
                createBackup: (data) => this.request('/api/hishaku/configs/backup', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                getBackups: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/hishaku/configs/backup${query}`);
                },
                getBackup: (backupId) => this.request(`/api/hishaku/configs/backup/${backupId}`),
                downloadBackup: (backupId, format = 'json') => {
                    const url = `${this.baseUrl}/api/hishaku/configs/backup/${backupId}/download?format=${format}`;
                    const headers = {};
                    if (this.token) {
                        headers.Authorization = `Bearer ${this.token}`;
                    }
                    // ファイルダウンロード用の処理
                    const link = document.createElement('a');
                    link.href = url;
                    link.style.display = 'none';
                    // 認証ヘッダーがある場合はfetchを使用
                    if (this.token) {
                        return fetch(url, { headers })
                            .then(response => {
                            if (!response.ok) {
                                throw new Error(`Download failed: ${response.statusText}`);
                            }
                            return response.blob();
                        })
                            .then(blob => {
                            const objectUrl = URL.createObjectURL(blob);
                            link.href = objectUrl;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(objectUrl);
                            return { success: true };
                        })
                            .catch(error => {
                            console.error('Download error:', error);
                            return { success: false, error: error.message };
                        });
                    }
                    else {
                        // 認証不要の場合は直接ダウンロード
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return Promise.resolve({ success: true });
                    }
                },
                deleteBackup: (backupId) => this.request(`/api/hishaku/configs/backup/${backupId}`, {
                    method: 'DELETE',
                }),
                // エクスポート・インポート
                exportConfigs: (data) => this.request('/api/hishaku/configs/export', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                importConfigs: (data) => this.request('/api/hishaku/configs/import', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                // 監査ログ
                getAuditLogs: (name, params) => {
                    const baseUrl = name ? `/api/hishaku/configs/${name}/audit` : '/api/hishaku/configs/audit';
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`${baseUrl}${query}`);
                },
            }
        });
        // Dataplane設定管理API
        Object.defineProperty(this, "dataplane", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // 基本CRUD
                getConfigs: () => this.request('/api/dataplane/configs'),
                getConfig: (name) => this.request(`/api/dataplane/configs/${name}`),
                createConfig: (config) => this.request('/api/dataplane/configs', {
                    method: 'POST',
                    body: JSON.stringify(config),
                }),
                updateConfig: (name, config) => this.request(`/api/dataplane/configs/${name}`, {
                    method: 'PUT',
                    body: JSON.stringify(config),
                }),
                deleteConfig: (name) => this.request(`/api/dataplane/configs/${name}`, {
                    method: 'DELETE',
                }),
                // バージョン管理
                getVersions: (name, params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/dataplane/configs/${name}/versions${query}`);
                },
                // バックアップ
                createBackup: (data) => this.request('/api/dataplane/configs/backup', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                getBackups: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/dataplane/configs/backup${query}`);
                },
                getBackup: (backupId) => this.request(`/api/dataplane/configs/backup/${backupId}`),
                downloadBackup: (backupId, format = 'json') => {
                    const url = `${this.baseUrl}/api/dataplane/configs/backup/${backupId}/download?format=${format}`;
                    const headers = {};
                    if (this.token) {
                        headers.Authorization = `Bearer ${this.token}`;
                    }
                    // ファイルダウンロード用の処理
                    const link = document.createElement('a');
                    link.href = url;
                    link.style.display = 'none';
                    // 認証ヘッダーがある場合はfetchを使用
                    if (this.token) {
                        return fetch(url, { headers })
                            .then(response => {
                            if (!response.ok) {
                                throw new Error(`Download failed: ${response.statusText}`);
                            }
                            return response.blob();
                        })
                            .then(blob => {
                            const objectUrl = URL.createObjectURL(blob);
                            link.href = objectUrl;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(objectUrl);
                            return { success: true };
                        })
                            .catch(error => {
                            console.error('Download error:', error);
                            return { success: false, error: error.message };
                        });
                    }
                    else {
                        // 認証不要の場合は直接ダウンロード
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return Promise.resolve({ success: true });
                    }
                },
                deleteBackup: (backupId) => this.request(`/api/dataplane/configs/backup/${backupId}`, {
                    method: 'DELETE',
                }),
                // エクスポート・インポート
                exportConfigs: (data) => this.request('/api/dataplane/configs/export', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                importConfigs: (data) => this.request('/api/dataplane/configs/import', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                // 監査ログ
                getAuditLogs: (name, params) => {
                    const baseUrl = name ? `/api/dataplane/configs/${name}/audit` : '/api/dataplane/configs/audit';
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`${baseUrl}${query}`);
                },
            }
        });
        // モニタリングAPI
        Object.defineProperty(this, "monitoring", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getSystem: () => this.request('/api/monitoring/system'),
                getPerformance: () => this.request('/api/monitoring/performance'),
                getSecurity: () => this.request('/api/monitoring/security'),
                getAvailability: () => this.request('/api/monitoring/availability'),
                getDashboard: () => this.request('/api/monitoring/dashboard'),
                recordMaintenance: (data) => this.request('/api/monitoring/maintenance', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
            }
        });
        // ログ管理API
        Object.defineProperty(this, "logs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                search: (params) => {
                    const query = `?${new URLSearchParams(params).toString()}`;
                    return this.request(`/api/logs/search${query}`);
                },
                getMetrics: () => this.request('/api/logs/metrics'),
                export: (data) => this.request('/api/logs/export', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                test: (data) => this.request('/api/logs/test', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
            }
        });
        // エラー管理API
        Object.defineProperty(this, "errors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getMetrics: () => this.request('/api/errors/metrics'),
                getHistory: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/errors/history${query}`);
                },
                getReports: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/errors/reports${query}`);
                },
                test: (data) => this.request('/api/errors/test', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
            }
        });
        // セキュリティAPI
        Object.defineProperty(this, "security", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getCSRFToken: () => this.request('/api/security/csrf-token'),
                getStats: () => this.request('/api/security/stats'),
                test: (data) => this.request('/api/security/test', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                updateConfig: (data) => this.request('/api/security/config', {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
            }
        });
        // APIキー管理API
        Object.defineProperty(this, "apiKeys", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getKeys: () => this.request('/api/apikeys'),
                createKey: (data) => this.request('/api/apikeys', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                deleteKey: (keyId) => this.request(`/api/apikeys/${keyId}`, {
                    method: 'DELETE',
                }),
            }
        });
        // プロンプト実行API
        Object.defineProperty(this, "prompt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                execute: (data) => this.request('/api/prompt/execute', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                getHistory: () => this.request('/api/prompt/history'),
                executeMizu: (data) => this.request('/api/prompt/execute/mizu', {
                    method: 'POST',
                    body: JSON.stringify({
                        prompt: data.prompt,
                        useHishaku: data.useHishaku || false,
                        mizuEndpoint: data.mizuEndpoint,
                        options: {
                            timeout: (data.timeout || 30) * 1000, // 秒をミリ秒に変換
                            headers: data.headers,
                            responseFormat: data.responseFormat
                        }
                    }),
                }),
            }
        });
        // Mizu統合API
        Object.defineProperty(this, "mizu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // サービス発見
                getServices: () => this.request('/api/mizu/services'),
                // エンドポイント管理
                getEndpoints: () => this.request('/api/mizu/endpoints'),
                getEndpoint: (id) => this.request(`/api/mizu/endpoints/${id}`),
                // MCP Tools
                getTools: () => this.request('/api/mizu/mcp/tools'),
                getTool: (toolId) => this.request(`/api/mizu/mcp/tools/${toolId}`),
                getToolExamples: (toolId) => this.request(`/api/mizu/mcp/tools/${toolId}/examples`),
                // Dive API（Phase 18.1-2 - Enhanced）
                dive: {
                    // 基本操作
                    health: () => this.request('/api/mizu/dive/health'),
                    listAgents: (params) => {
                        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                        return this.request(`/api/mizu/dive/agents${query}`);
                    },
                    getAgent: (agentId) => this.request(`/api/mizu/dive/agents/${agentId}`),
                    createAgent: (payload) => this.request('/api/mizu/dive/agents', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    }),
                    updateAgent: (agentId, payload) => this.request(`/api/mizu/dive/agents/${agentId}`, {
                        method: 'PUT',
                        body: JSON.stringify(payload),
                    }),
                    deleteAgent: (agentId) => this.request(`/api/mizu/dive/agents/${agentId}`, {
                        method: 'DELETE',
                    }),
                    // RAG コレクション管理
                    listCollections: (params) => {
                        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                        return this.request(`/api/mizu/dive/collections${query}`);
                    },
                    getCollection: (collectionId) => this.request(`/api/mizu/dive/collections/${collectionId}`),
                    createCollection: (payload) => this.request('/api/mizu/dive/collections', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    }),
                    // ドキュメント追加
                    addDocuments: (collectionName, formData) => this.request(`/api/mizu/dive/collections/${encodeURIComponent(collectionName)}/documents`, {
                        method: 'POST',
                        body: formData, // multipart/form-data
                    }),
                    // LLM モデル一覧取得（Kusanagi API）
                    listLLMs: () => this.request('/api/mizu/dive/llms'),
                    // MCP ツール管理
                    getTool: (toolId) => this.request(`/api/mizu/dive/tools/${toolId}`),
                    // Thread管理
                    createThread: (agentId, payload) => this.request('/api/mizu/dive/threads', {
                        method: 'POST',
                        body: JSON.stringify({ agent_id: agentId, ...payload }),
                    }),
                    getThread: (threadId) => this.request(`/api/mizu/dive/threads/${threadId}`),
                    // Run管理
                    createRun: (threadId, payload) => this.request('/api/mizu/dive/runs', {
                        method: 'POST',
                        body: JSON.stringify({ thread_id: threadId, ...payload }),
                    }),
                    getRun: (runId) => this.request(`/api/mizu/dive/runs/${runId}`),
                    listRuns: (threadId, params) => {
                        const baseEndpoint = threadId
                            ? `/api/mizu/dive/threads/${threadId}/runs`
                            : '/api/mizu/dive/runs';
                        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                        return this.request(`${baseEndpoint}${query}`);
                    },
                },
            }
        });
        // 開発用API（Studio専用）
        Object.defineProperty(this, "dev", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getOverview: (agentId) => this.request(`/api/dev/agents/${agentId}/overview`),
                updatePrompt: (agentId, data) => this.request(`/api/dev/agents/${agentId}/prompt`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                updateBasic: (agentId, data) => this.request(`/api/dev/agents/${agentId}/basic`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                listRagCollections: (agentId, params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/dev/agents/${agentId}/rag/collections${query}`);
                },
                getRag: (agentId) => this.request(`/api/dev/agents/${agentId}/rag`),
                updateRag: (agentId, data) => this.request(`/api/dev/agents/${agentId}/rag`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                getPermissions: (agentId) => this.request(`/api/dev/agents/${agentId}/permissions`),
                updatePermissions: (agentId, data) => this.request(`/api/dev/agents/${agentId}/permissions`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                getMcp: (agentId) => this.request(`/api/dev/agents/${agentId}/mcp`),
                updateMcp: (agentId, data) => this.request(`/api/dev/agents/${agentId}/mcp`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                listVersions: (agentId, limit = 20) => this.request(`/api/dev/agents/${agentId}/versions?limit=${limit}`),
                restoreVersion: (agentId, versionId) => this.request(`/api/dev/agents/${agentId}/versions/${versionId}/restore`, { method: 'POST' }),
                // RAGコレクション管理
                createRagCollection: (data) => this.request('/api/dev/rag/collections', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                addDocumentsToCollection: (collectionName, files) => this.request(`/api/dev/rag/collections/${encodeURIComponent(collectionName)}/documents`, {
                    method: 'POST',
                    body: JSON.stringify({ files }),
                }),
            }
        });
        // ==========================================
        // Phase 17.1.1: 統合検索システムAPI
        // ==========================================
        // 統合検索API
        Object.defineProperty(this, "search", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // 統合検索
                unified: (request) => this.request('/api/v1/search/unified', {
                    method: 'POST',
                    body: JSON.stringify(request),
                }),
                // 保存済み検索
                saved: {
                    list: () => this.request('/api/v1/search/saved'),
                    create: (search) => this.request('/api/v1/search/saved', {
                        method: 'POST',
                        body: JSON.stringify(search),
                    }),
                    get: (searchId) => this.request(`/api/v1/search/saved/${searchId}`),
                    update: (searchId, search) => this.request(`/api/v1/search/saved/${searchId}`, {
                        method: 'PUT',
                        body: JSON.stringify(search),
                    }),
                    delete: (searchId) => this.request(`/api/v1/search/saved/${searchId}`, {
                        method: 'DELETE',
                    }),
                    execute: (searchId) => this.request(`/api/v1/search/saved/${searchId}/execute`, {
                        method: 'POST',
                    }),
                },
                // クイックフィルタ
                filters: {
                    list: () => this.request('/api/v1/search/filters'),
                    create: (filter) => this.request('/api/v1/search/filters', {
                        method: 'POST',
                        body: JSON.stringify(filter),
                    }),
                    update: (filterId, filter) => this.request(`/api/v1/search/filters/${filterId}`, {
                        method: 'PUT',
                        body: JSON.stringify(filter),
                    }),
                    delete: (filterId) => this.request(`/api/v1/search/filters/${filterId}`, {
                        method: 'DELETE',
                    }),
                },
                // 検索推奨
                suggestions: {
                    get: (query, limit) => {
                        const params = new URLSearchParams({ query });
                        if (limit)
                            params.append('limit', limit.toString());
                        return this.request(`/api/v1/search/suggestions?${params}`);
                    },
                    popular: (limit) => {
                        const params = new URLSearchParams();
                        if (limit)
                            params.append('limit', limit.toString());
                        return this.request(`/api/v1/search/suggestions/popular?${params}`);
                    },
                },
                // 検索分析
                analytics: {
                    get: (from, to) => {
                        const params = new URLSearchParams();
                        if (from)
                            params.append('from', from);
                        if (to)
                            params.append('to', to);
                        return this.request(`/api/v1/search/analytics?${params}`);
                    },
                },
                // 検索インデックス管理
                index: {
                    status: () => this.request('/api/v1/search/index/status'),
                    rebuild: (dataSource) => this.request(`/api/v1/search/index/rebuild/${dataSource}`, {
                        method: 'POST',
                    }),
                    optimize: (dataSource) => this.request(`/api/v1/search/index/optimize/${dataSource}`, {
                        method: 'POST',
                    }),
                },
            }
        });
        // カスタムエンドポイント管理API - Phase 2
        Object.defineProperty(this, "customEndpoints", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // 基本CRUD
                getEndpoints: () => this.request('/api/custom-endpoints'),
                getEndpoint: (id) => this.request(`/api/custom-endpoints/${id}`),
                createEndpoint: (data) => this.request('/api/custom-endpoints', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                updateEndpoint: (id, data) => this.request(`/api/custom-endpoints/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                deleteEndpoint: (id) => this.request(`/api/custom-endpoints/${id}`, {
                    method: 'DELETE',
                }),
                // 実行
                executeEndpoint: (id, data) => this.request(`/api/custom-endpoints/${id}/execute`, {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
            }
        });
        // AI エージェント管理API
        Object.defineProperty(this, "aiAgents", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // 基本CRUD
                getAgents: () => this.request('/api/ai-agents'),
                getAgent: (id) => this.request(`/api/ai-agents/${id}`),
                createAgent: (data) => this.request('/api/ai-agents', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                updateAgent: (id, data) => this.request(`/api/ai-agents/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                }),
                deleteAgent: (id) => this.request(`/api/ai-agents/${id}`, {
                    method: 'DELETE',
                }),
                // 実行
                executeAgent: (id, data) => this.request(`/api/ai-agents/${id}/execute`, {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
            }
        });
        // 新しいSusugiワークフロー管理API
        Object.defineProperty(this, "susugi", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // バッチジョブ管理（レガシー）
                getBatchJobs: () => this.request('/api/v1/batch/jobs'),
                getBatchJob: (jobId) => this.request(`/api/v1/batch/jobs/${jobId}`),
                createBatchJob: (data) => this.request('/api/v1/batch/jobs', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                startBatchJob: (jobId) => this.request(`/api/v1/batch/jobs/${jobId}/start`, {
                    method: 'POST',
                }),
                stopBatchJob: (jobId) => this.request(`/api/v1/batch/jobs/${jobId}/cancel`, {
                    method: 'POST',
                }),
                // ワークフロー管理（レガシー）
                getWorkflowStatus: () => this.request('/api/v1/workflows/status'),
                getWorkflowResults: (workflowId) => this.request(`/api/v1/workflows/${workflowId}/results`),
                // 設定管理
                getBatchSettings: () => this.request('/api/v1/config/batch-settings'),
                updateBatchSettings: (data) => this.request('/api/v1/config/batch-settings', {
                    method: 'POST',
                    body: JSON.stringify(data),
                }),
                // === テナント分離ETLワークフロー管理API ===
                // テナント管理
                getTenants: () => this.request('/api/v1/workflows/tenants'),
                getTenant: (tenantId) => this.request(`/api/v1/workflows/${tenantId}/info`),
                // レガシーワークフロー（テナント分離版）
                legacy: {
                    getBatchJobs: (tenantId) => this.request(`/api/v1/workflows/${tenantId}/batch/jobs`),
                    createBatchJob: (tenantId, data) => this.request(`/api/v1/workflows/${tenantId}/batch/jobs`, {
                        method: 'POST',
                        body: JSON.stringify(data),
                    }),
                    startBatchJob: (tenantId, jobId) => this.request(`/api/v1/workflows/${tenantId}/batch/jobs/${jobId}/start`, {
                        method: 'POST',
                    }),
                    stopBatchJob: (tenantId, jobId) => this.request(`/api/v1/workflows/${tenantId}/batch/jobs/${jobId}/cancel`, {
                        method: 'POST',
                    }),
                    getWorkflowStatus: (tenantId) => this.request(`/api/v1/workflows/${tenantId}/status`),
                    getWorkflowResults: (tenantId, workflowId) => this.request(`/api/v1/workflows/${tenantId}/${workflowId}/results`),
                },
                // ETLワークフロー管理
                etl: {
                    // ワークフロー定義
                    getWorkflows: async (_tenantId) => {
                        const response = await this.request('/api/v1/workflows', {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        const workflows = (response.data.items ?? []).map((item) => normalizeWorkflowDefinition(item, this.getWorkflowViewOverride(item.workflow_id)));
                        return {
                            success: true,
                            data: {
                                workflows,
                                total: response.data.total ?? response.data.items?.length ?? 0,
                            },
                        };
                    },
                    createWorkflow: async (_tenantId, workflow) => {
                        const response = await this.request('/api/v1/workflows', {
                            method: 'POST',
                            headers: buildWorkflowTenantHeaders(_tenantId),
                            body: JSON.stringify(workflow),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        return {
                            success: true,
                            data: {
                                workflow: normalizeWorkflowDefinition(response.data, this.getWorkflowViewOverride(response.data.workflow_id)),
                                message: 'created',
                            },
                        };
                    },
                    getWorkflow: async (_tenantId, workflowId) => {
                        const response = await this.request(`/api/v1/workflows/${workflowId}`, {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        return {
                            success: true,
                            data: {
                                workflow: normalizeWorkflowDefinition(response.data, this.getWorkflowViewOverride(workflowId)),
                            },
                        };
                    },
                    updateWorkflow: async (_tenantId, workflowId, workflow) => {
                        const tenantHeaders = buildWorkflowTenantHeaders(_tenantId);
                        const currentWorkflowResponse = await this.request(`/api/v1/workflows/${workflowId}`, {
                            headers: tenantHeaders,
                        });
                        const currentWorkflow = currentWorkflowResponse.success && currentWorkflowResponse.data
                            ? normalizeWorkflowDefinition(currentWorkflowResponse.data, this.getWorkflowViewOverride(workflowId))
                            : undefined;
                        const mergedWorkflow = {
                            ...currentWorkflow,
                            ...workflow,
                        };
                        const response = await this.request('/api/v1/workflows', {
                            method: 'POST',
                            headers: tenantHeaders,
                            body: JSON.stringify({
                                workflow_id: workflowId,
                                name: mergedWorkflow.name ?? 'Untitled workflow',
                                description: mergedWorkflow.description ?? '',
                                steps: mergedWorkflow.steps ?? [],
                                tags: mergedWorkflow.tags ?? [],
                            }),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        const normalizedWorkflow = normalizeWorkflowDefinition(response.data, mergedWorkflow);
                        this.setWorkflowViewOverride(workflowId, {
                            is_active: normalizedWorkflow.is_active,
                        });
                        return {
                            success: true,
                            data: {
                                workflow: normalizedWorkflow,
                                message: 'updated',
                            },
                        };
                    },
                    deleteWorkflow: (_tenantId, workflowId) => this.delete(`/api/v1/workflows/${workflowId}`, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    // ワークフロー実行
                    executeWorkflow: (tenantId, workflowId, executionConfig) => this.request(`/api/v1/workflows/${workflowId}/executions`, {
                        method: 'POST',
                        headers: buildWorkflowTenantHeaders(tenantId),
                        body: JSON.stringify({ parameters: executionConfig?.override_config ?? {} }),
                    }),
                    // 実行状況監視
                    getExecutionStatus: async (_tenantId, executionId) => {
                        const response = await this.request(`/api/v1/workflow-executions/${executionId}`, {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        return {
                            success: true,
                            data: normalizeExecutionSummary(response.data, this.tenantId || ''),
                        };
                    },
                    getExecutionResult: async (_tenantId, executionId) => {
                        const response = await this.request(`/api/v1/workflow-executions/${executionId}/result`, {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        return {
                            success: true,
                            data: normalizeExecutionSummary(response.data, this.tenantId || ''),
                        };
                    },
                    getExecutionFinalResult: async (_tenantId, executionId) => {
                        return this.request(`/api/v1/workflow-executions/${executionId}/final-result`, {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                    },
                    listExecutions: async (_tenantId, params) => {
                        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                        const response = await this.request(`/api/v1/workflow-executions${query}`, {
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                        if (!response.success || !response.data) {
                            return response;
                        }
                        return {
                            success: true,
                            data: {
                                executions: response.data.items.map((item) => normalizeExecutionSummary(item, this.tenantId || '')),
                                total: response.data.total,
                                page: response.data.page,
                                limit: response.data.limit,
                            },
                        };
                    },
                    cancelExecution: (_tenantId, executionId) => this.request(`/api/v1/workflow-executions/${executionId}/cancel`, {
                        method: 'POST',
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    listSchedules: (_tenantId, params) => {
                        return this.get('/api/v1/workflow-schedules', {
                            params: params,
                            headers: buildWorkflowTenantHeaders(_tenantId),
                        });
                    },
                    getSchedule: (_tenantId, scheduleId) => this.request(`/api/v1/workflow-schedules/${scheduleId}`, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    getScheduleDetail: (_tenantId, scheduleId) => this.request(`/api/v1/workflow-schedules/${scheduleId}/detail`, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    createSchedule: (_tenantId, schedule) => this.post('/api/v1/workflow-schedules', {
                        workflow_id: schedule.workflow_id,
                        name: schedule.name,
                        description: schedule.description,
                        trigger: {
                            trigger_type: 'cron',
                            cron: schedule.cron_expression,
                            timezone: schedule.timezone,
                        },
                    }, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    updateSchedule: (_tenantId, scheduleId, schedule) => this.request(`/api/v1/workflow-schedules/${scheduleId}`, {
                        method: 'PATCH',
                        headers: buildWorkflowTenantHeaders(_tenantId),
                        body: JSON.stringify({
                            name: schedule.name,
                            description: schedule.description,
                            trigger: {
                                trigger_type: 'cron',
                                cron: schedule.cron_expression,
                                timezone: schedule.timezone,
                            },
                        }),
                    }),
                    pauseSchedule: (_tenantId, scheduleId) => this.post(`/api/v1/workflow-schedules/${scheduleId}/pause`, undefined, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    resumeSchedule: (_tenantId, scheduleId) => this.post(`/api/v1/workflow-schedules/${scheduleId}/resume`, undefined, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    deleteSchedule: (_tenantId, scheduleId) => this.delete(`/api/v1/workflow-schedules/${scheduleId}`, {
                        headers: buildWorkflowTenantHeaders(_tenantId),
                    }),
                    hydrateScheduleCard: (schedule, workflowName, detail) => hydrateScheduleCard(schedule, workflowName, detail),
                },
                // ファイル管理
                files: {
                    uploadFile: async (tenantId, file, metadata) => {
                        const formData = new FormData();
                        formData.append('file', file);
                        if (metadata) {
                            formData.append('metadata', JSON.stringify(metadata));
                        }
                        return this.request(`/api/v1/workflows/${tenantId}/files/upload`, {
                            method: 'POST',
                            body: formData,
                            headers: buildWorkflowTenantHeaders(tenantId),
                        });
                    },
                    getFiles: async (tenantId, params) => {
                        return this.get(`/api/v1/workflows/${tenantId}/files`, {
                            params,
                            headers: buildWorkflowTenantHeaders(tenantId),
                        });
                    },
                    getFile: async (tenantId, fileId) => this.request(`/api/v1/workflows/${tenantId}/files/${fileId}`, {
                        headers: buildWorkflowTenantHeaders(tenantId),
                    }),
                    deleteFile: async (tenantId, fileId) => this.delete(`/api/v1/workflows/${tenantId}/files/${fileId}`, {
                        headers: buildWorkflowTenantHeaders(tenantId),
                    }),
                },
                // エージェント管理
                agents: {
                    getAgents: (tenantId) => this.request(`/api/v1/workflows/${tenantId}/agents`),
                    createAgent: (tenantId, agent) => this.request(`/api/v1/workflows/${tenantId}/agents`, {
                        method: 'POST',
                        body: JSON.stringify(agent),
                    }),
                    getAgent: (tenantId, agentId) => this.request(`/api/v1/workflows/${tenantId}/agents/${agentId}`),
                    updateAgent: (tenantId, agentId, agent) => this.request(`/api/v1/workflows/${tenantId}/agents/${agentId}`, {
                        method: 'PUT',
                        body: JSON.stringify(agent),
                    }),
                    deleteAgent: (tenantId, agentId) => this.request(`/api/v1/workflows/${tenantId}/agents/${agentId}`, {
                        method: 'DELETE',
                    }),
                },
            }
        });
        // ヘルスチェック
        Object.defineProperty(this, "health", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                check: () => this.request('/health'),
            }
        });
        // Shibuki通知管理API
        Object.defineProperty(this, "shibuki", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // プロバイダー管理
                getProviders: () => this.request('/api/v1/providers'),
                testProvider: (provider) => this.request(`/api/v1/providers/${provider}/test`, {
                    method: 'POST',
                }),
                // チャネル管理
                getChannelDefaults: () => this.request('/api/v1/channels/defaults'),
                getChannelStatus: () => this.request('/api/v1/channels/status'),
                getChannelRouting: () => this.request('/api/v1/channels/routing'),
                // テンプレート管理
                getTemplates: () => this.request('/api/v1/templates'),
                // 通知送信
                createNotification: (notification) => this.request('/api/v1/notifications', {
                    method: 'POST',
                    body: JSON.stringify(notification),
                }),
                getNotifications: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/v1/notifications${query}`);
                },
            }
        });
        // Google Chat Spaces管理API
        Object.defineProperty(this, "googleChatSpaces", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                // Space管理
                getSpaces: () => this.request('/api/v1/spaces'),
                createSpace: (spaceData) => this.request('/api/v1/spaces', {
                    method: 'POST',
                    body: JSON.stringify(spaceData),
                }),
                updateSpace: (spaceId, spaceData) => this.request(`/api/v1/spaces/${spaceId}`, {
                    method: 'PUT',
                    body: JSON.stringify(spaceData),
                }),
                deleteSpace: (spaceId) => this.request(`/api/v1/spaces/${spaceId}`, {
                    method: 'DELETE',
                }),
                testSpace: (spaceId) => this.request(`/api/v1/spaces/${spaceId}/test`, {
                    method: 'POST',
                }),
            }
        });
        // Kujyu社内データ取得API
        Object.defineProperty(this, "kujyu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                getDataSources: () => this.request('/api/mizu/services'),
                getData: (request) => this.request('/api/mizu/endpoints', {
                    method: 'POST',
                    body: JSON.stringify(request),
                }),
                getWeeklyReports: (params) => {
                    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
                    return this.request(`/api/v1/weekly-reports${query}`);
                },
                getEmployeeByCode: (code) => this.request(`/api/v1/employees/number/${encodeURIComponent(code)}`),
                searchEmployees: (search, page = 1, size = 20) => this.request(`/api/v1/employees?search=${encodeURIComponent(search)}&page=${page}&size=${size}&sort=employeeNumber&direction=ASC`),
                listEmployees: (page = 1, size = 10, sort = 'hireDate', direction = 'DESC') => this.request(`/api/v1/employees?page=${page}&size=${size}&sort=${sort}&direction=${direction}`),
            }
        });
        this.baseUrl = baseUrl;
    }
    setToken(token) {
        this.token = token;
    }
    setAuthRefreshHandler(handler) {
        this.refreshAuthHandler = handler;
    }
    setTenantId(tenantId) {
        if (this.tenantId !== tenantId) {
            this.workflowViewOverrides.clear();
        }
        this.tenantId = tenantId;
    }
    workflowOverrideKey(workflowId) {
        return `${this.tenantId ?? 'default'}:${workflowId}`;
    }
    getWorkflowViewOverride(workflowId) {
        return this.workflowViewOverrides.get(this.workflowOverrideKey(workflowId)) ?? {};
    }
    setWorkflowViewOverride(workflowId, override) {
        const key = this.workflowOverrideKey(workflowId);
        const current = this.workflowViewOverrides.get(key) ?? {};
        this.workflowViewOverrides.set(key, {
            ...current,
            ...override,
        });
    }
    // 🔍 TRACE: 統一認証クリア処理（呼び出し元追跡）
    clearAuth() {
        console.log('🔍 [AUTH-CLEAR-TRACE] clearAuth() called - トークンクリア実行');
        console.log('🔍 [AUTH-CLEAR-TRACE] Call stack:', new Error().stack);
        this.setToken(null);
        if (typeof window !== 'undefined') {
            const beforeToken = localStorage.getItem('teico_token');
            console.log('🔍 [AUTH-CLEAR-TRACE] Before clear - token:', beforeToken ? `${beforeToken.substring(0, 50)}...` : 'NULL');
            localStorage.removeItem('teico_token');
            localStorage.removeItem('teico_user');
            localStorage.removeItem('teico_tenant_id');
            localStorage.removeItem('teico_refresh_token');
            localStorage.removeItem('teico_id_token');
            localStorage.removeItem('teico_oidc_session');
            console.log('🔍 [AUTH-CLEAR-TRACE] After clear - localStorage cleared');
        }
    }
    async refreshAuthForRetry(endpoint) {
        if (!this.refreshAuthHandler) {
            return false;
        }
        const refreshExcludedPaths = [
            '/auth/login',
            '/auth/register',
            '/auth/logout',
            '/auth/oauth/refresh',
            '/auth/oauth/session',
            '/auth/oauth/authorize',
        ];
        if (refreshExcludedPaths.some((path) => endpoint.includes(path))) {
            return false;
        }
        try {
            console.warn('Authentication failed - attempting token refresh before clearing credentials');
            return await this.refreshAuthHandler();
        }
        catch (error) {
            console.error('Authentication refresh retry failed:', error);
            return false;
        }
    }
    // 統一エラーハンドリング
    handleApiError(error, context, response) {
        const status = response?.status;
        // 401エラー特別処理
        if (status === 401) {
            console.error('Authentication failed - clearing credentials');
            this.clearAuth();
            return {
                success: false,
                error: 'Authentication required',
            };
        }
        // その他のエラー
        const errorMessage = error?.message || error || 'Unknown error';
        console.error('API Error:', errorMessage, `(Status: ${status || 'Network'})`);
        return {
            success: false,
            error: errorMessage,
        };
    }
    async request(endpoint, options = {}, allowAuthRefresh = true) {
        // 絶対URLかどうかを判定
        const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
        const url = isAbsoluteUrl ? endpoint : `${this.baseUrl}${endpoint}`;
        const headers = {
            ...options.headers,
        };
        // UI-5.1-B修正: FormDataの場合はContent-Typeを自動設定・それ以外はJSON
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        // 認証ヘッダーの確認と設定
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }
        else {
            // 認証なしリクエストの場合のみ警告表示
            if (!endpoint.includes('/login') && !endpoint.includes('/auth/')) {
                console.warn('No auth token - unauthenticated request for:', endpoint);
            }
        }
        // テナントIDをX-Tenant-IDヘッダーとして送信（セキュリティ: テナント分離のため）
        // 優先順位: 1) apiClientに設定されたtenantId, 2) URLパスから抽出
        let effectiveTenantId = null;
        if (this.tenantId) {
            effectiveTenantId = this.tenantId;
        }
        else {
            // URLパスから抽出（フォールバック）
            const tenantIdMatch = endpoint.match(/\/api\/v1\/(?:workflows\/)?([^\/]+)/);
            if (tenantIdMatch && tenantIdMatch[1] && tenantIdMatch[1] !== 'workflows') {
                effectiveTenantId = tenantIdMatch[1];
            }
        }
        // テナントIDが取得できた場合は必ずX-Tenant-IDヘッダーを送信（テナント分離のため）
        // defaultも含めて送信する（バックエンドで検証可能にする）
        if (effectiveTenantId && !headers['X-Tenant-ID']) {
            headers['X-Tenant-ID'] = effectiveTenantId;
        }
        try {
            const response = await fetch(url, {
                ...options,
                headers,
            });
            // Content-Typeを確認
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const isHtml = contentType.includes('text/html');
            if (!response.ok) {
                if (response.status === 401 && allowAuthRefresh && await this.refreshAuthForRetry(endpoint)) {
                    return this.request(endpoint, options, false);
                }
                let errorMessage = `API Error: ${response.status}`;
                // 詳細なエラーメッセージを取得
                if (isJson) {
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.error || errorMessage;
                    }
                    catch {
                        // JSON解析に失敗した場合はデフォルトメッセージを使用
                    }
                }
                else if (isHtml) {
                    // HTMLレスポンスの場合は認証エラーの可能性が高い
                    if (response.status === 401 || response.status === 403) {
                        errorMessage = 'Authentication required';
                    }
                    else {
                        errorMessage = `Server returned HTML (Status: ${response.status})`;
                    }
                }
                // 統一エラーハンドリング使用
                return this.handleApiError(errorMessage, endpoint, response);
            }
            // HTMLレスポンスの場合はエラーとして処理
            if (isHtml) {
                return this.handleApiError('Server returned HTML instead of JSON', endpoint, response);
            }
            // レスポンスデータを取得
            let data;
            if (isJson) {
                try {
                    data = await response.json();
                }
                catch (error) {
                    // JSON解析エラーの場合
                    return this.handleApiError('Invalid JSON response', endpoint, response);
                }
            }
            else {
                // JSON以外のレスポンスの場合はテキストとして取得
                const text = await response.text();
                return {
                    success: true,
                    data: text,
                };
            }
            // サーバーが既にApiResponse形式を返している場合はそのまま返す
            if (data && typeof data === 'object' && 'success' in data) {
                return data;
            }
            // そうでない場合は success: true を追加
            return {
                success: true,
                data: data,
            };
        }
        catch (error) {
            // 🎯 Phase 1: 統一エラーハンドリング使用
            return this.handleApiError(error, endpoint);
        }
    }
    // 基本的なHTTPメソッド
    async get(url, options) {
        const queryParams = options?.params ? `?${new URLSearchParams(options.params).toString()}` : '';
        return this.request(`${url}${queryParams}`, { method: 'GET', headers: options?.headers });
    }
    async post(url, data, options) {
        return this.request(url, {
            method: 'POST',
            headers: options?.headers,
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async put(url, data, options) {
        return this.request(url, {
            method: 'PUT',
            headers: options?.headers,
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async delete(url, options) {
        return this.request(url, { method: 'DELETE', headers: options?.headers });
    }
}
export const apiClient = new ApiClient();
